import React, { useEffect } from 'react'
import { IAlarm, ICamera } from '@obvious.tech/constellation'

import api, { TApiError, TApiResponse, TEntity } from 'src/utils/api'
import { TLoadable } from 'src/utils/Loadable'

const getCamerasByIDs = async ({
  ids,
  token,
}: {
  ids: string[]
  token?: string
}): Promise<TApiResponse<Array<TEntity<ICamera>>, TApiError>> => {
  if (ids.length === 0) {
    return await Promise.resolve<TApiResponse<Array<TEntity<ICamera>>, never>>({
      _tag: 'success',
      data: [],
    })
  }
  return await api.getResourcesWithFilters<TEntity<ICamera>>(
    'videomanager/api/Camera',
  )({
    token,
    filters: [['id', api.Filter.Eq, ids]],
  })
}

type TProps = {
  alarm: TEntity<IAlarm>
  authorization?: string
}

export type TUseAdditionalDetailsCamerasResult = TLoadable<
  Array<TEntity<ICamera>>,
  TApiError
>

const useAdditionalDetailsCameras = ({
  alarm,
  authorization,
}: TProps): TUseAdditionalDetailsCamerasResult => {
  const [cameras, getCameras] = api.useApiAction(getCamerasByIDs)

  const cameraIds = React.useMemo(() => {
    return (alarm.additionalDetails ?? []).reduce<string[]>((acc, detail) => {
      if (detail.type === 'CameraData' && typeof detail.data?.ID === 'string')
        acc.push(detail.data.ID)

      return acc
    }, [])
  }, [alarm])

  useEffect(() => {
    return getCameras({ ids: cameraIds, token: authorization })
  }, [cameraIds, getCameras, authorization])

  return cameras
}

export default useAdditionalDetailsCameras
